import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ApiError } from '@/lib/api'
import { getSessionEpoch } from '@/lib/auth'
import { PERMISSION_KEYS } from '@/lib/permissions'
import { usePermissionsAt } from '@/lib/use-permissions'
import {
  PolicyPositionPicker,
  PolicySelector,
  PRINTER_SELECTOR_FIELDS,
  usePolicyOrder,
  type SelectorGui,
} from '@/modules/hierarchy'
import { Button } from '@/ui/button'
import { DialogTitle } from '@/ui/dialog'
import { Frame, FrameCard } from '@/ui/frame'
import { LoadingNotice } from '@/ui/loading-notice'
import { RetryNotice } from '@/ui/retry-notice'
import { TextField } from '@/ui/text-field'
import { getAlertCapabilities } from '../../api/alert-rules-api'
import {
  PRINTER_EVENT_RULE_POLICY_TYPE,
  type EventRuleContent,
  type EventRulePolicyEdit,
  type PrinterEventRulePolicy,
} from '../../api/event-rule-policies-api'
import {
  builderCatalog,
  type BuilderCatalog,
} from '../alert-rules/alert-condition'
import { EventConditionFields } from './event-condition-fields'
import {
  consumableDocument,
  defaultConsumableCondition,
  readConsumableCondition,
} from './event-condition-model'
import { EventNotificationActions } from './event-notification-actions'
import {
  actionContent,
  actionDraft,
  MAX_NOTIFICATION_RECIPIENTS,
  newEventRuleId,
} from './event-action-model'
import { useSaveEventRulePolicy } from './event-rule-policy-queries'

type FormProps = {
  entityId: string
  policy: PrinterEventRulePolicy | null
  onClose: () => void
  onReload?: () => void
  reloading?: boolean
  reloadError?: string | null
}

export function EventRulePolicyForm(props: FormProps) {
  const capabilities = useQuery({
    queryKey: [
      'assets',
      'alert-capabilities',
      getSessionEpoch(),
      props.entityId,
    ],
    queryFn: ({ signal }) => getAlertCapabilities(props.entityId, signal),
  })
  if (!capabilities.data)
    return capabilities.isError ? (
      <RetryNotice
        message="Event conditions could not be loaded."
        onRetry={() => void capabilities.refetch()}
      />
    ) : (
      <LoadingNotice message="Loading event conditions…" />
    )
  const catalog = builderCatalog(capabilities.data)
  if (
    !catalog ||
    catalog.assetType !== 'printer' ||
    catalog.event !== 'consumable.updated' ||
    !catalog.fields.some((field) => field.id === 'supply.percentRemaining')
  ) {
    return (
      <p role="alert" className="text-xs">
        Printer consumable conditions are not available.
      </p>
    )
  }
  return <EventRulePolicyFields {...props} catalog={catalog} />
}

function editingRestriction(
  actionsRedacted: boolean,
  emailLocked: boolean,
  unsupportedActions: boolean,
) {
  if (actionsRedacted)
    return 'Action details are hidden. Editing requires permission to manage event rules.'
  if (emailLocked)
    return 'Editing this rule requires email-recipient permission.'
  if (unsupportedActions)
    return 'This rule contains actions this editor cannot change. Its saved content is preserved.'
  return null
}

export function EventRulePolicyFields({
  entityId,
  policy,
  onClose,
  onReload,
  reloading = false,
  reloadError,
  catalog,
}: FormProps & { catalog: BuilderCatalog }) {
  const permissions = usePermissionsAt(entityId)
  const canAddEmail = permissions.can(PERMISSION_KEYS.alertEmailManage)
  const canManage = permissions.can(PERMISSION_KEYS.alertManage)
  const save = useSaveEventRulePolicy(entityId)
  const order = usePolicyOrder(
    entityId,
    PRINTER_EVENT_RULE_POLICY_TYPE,
    policy === null,
  )
  const [operationId] = useState(() => crypto.randomUUID())
  const [ruleId] = useState(newEventRuleId)
  const [name, setName] = useState(policy?.name ?? '')
  const [condition, setCondition] = useState(() => {
    if (!policy) return defaultConsumableCondition()
    if (policy.rule.content.detectorType !== 'printer.consumable.updated')
      return null
    return readConsumableCondition(catalog, policy.rule.content.configuration)
  })
  const [actions, setActions] = useState(() =>
    (policy?.rule.content.actions ?? []).map(actionDraft),
  )
  const [selectorExpression, setSelectorExpression] = useState(
    policy?.selectorExpression ?? '',
  )
  const [selectorGui, setSelectorGui] = useState<unknown>(
    policy?.selectorGui ?? null,
  )
  const [selectorValid, setSelectorValid] = useState(true)
  const [beforePolicyId, setBeforePolicyId] = useState<string | null>(null)
  const [attemptedCreate, setAttemptedCreate] =
    useState<EventRulePolicyEdit | null>(null)
  const [uncertain, setUncertain] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const unsupportedActions = actions.some(
    (action) =>
      action.type !== 'notification' ||
      action.recipients.some(
        (recipient) => !['user', 'group', 'email'].includes(recipient.type),
      ),
  )
  const showActions = !unsupportedActions && (policy !== null || Boolean(condition?.threshold.field))
  const emailLocked =
    !canAddEmail &&
    actions.some((action) =>
      action.recipients.some((recipient) => recipient.type === 'email'),
    )
  const protectedContent =
    policy?.actionsRedacted || unsupportedActions || emailLocked
  const locked =
    save.isPending || reloading || uncertain || !canManage || protectedContent
  const orderReady =
    policy !== null || (order.data !== undefined && !order.isFetching)
  const orderConflict =
    !policy &&
    !uncertain &&
    save.error instanceof ApiError &&
    save.error.code === 'POLICY_ORDER_CONFLICT'
  const updateConflict =
    policy !== null &&
    save.error instanceof ApiError &&
    save.error.status === 409
  const saveErrorMessage =
    orderConflict || updateConflict
      ? 'This policy or its order changed. Reload it before saving again.'
      : save.error?.message
  const title = policy ? `Edit ${policy.name}` : '2. Define the event rule'
  const restriction = editingRestriction(
    policy?.actionsRedacted ?? false,
    emailLocked,
    unsupportedActions,
  )
  let saveLabel = 'Save policy'
  if (uncertain) saveLabel = 'Retry same policy'
  if (save.isPending) saveLabel = 'Saving…'

  function buildEdit(): EventRulePolicyEdit | null {
    if (!name.trim() || !selectorValid) {
      setLocalError('Enter a policy name and complete the target selector.')
      return null
    }
    const serialized = condition ? consumableDocument(catalog, condition) : null
    if (serialized && serialized.problems.size > 0) {
      setLocalError('Complete every event condition and supply filter.')
      return null
    }
    const invalidRecipients = actions.some(
      (action) =>
        action.recipients.length === 0 ||
        action.recipients.length > MAX_NOTIFICATION_RECIPIENTS,
    )
    if (invalidRecipients) {
      setLocalError(
        `Choose between 1 and ${MAX_NOTIFICATION_RECIPIENTS} recipients for each notification action.`,
      )
      return null
    }
    const notifications = actionContent(actions)
    let content: EventRuleContent
    if (serialized) {
      content = {
        detectorType: 'printer.consumable.updated',
        configuration: serialized.document,
        actions: notifications,
      }
    } else if (policy) {
      content = { ...policy.rule.content, actions: notifications }
    } else {
      return null
    }
    return {
      name: name.trim(),
      content,
      selectorExpression: selectorExpression.trim() || null,
      selectorGui: selectorExpression.trim() ? selectorGui : null,
      overridesPolicyId: policy?.overridesPolicyId ?? null,
      ...(policy === null && order.data
        ? {
            placement: {
              atStart: false,
              beforePolicyId,
              expectedOrder: order.data.map(({ id, revision }) => ({
                id,
                revision,
              })),
            },
          }
        : {}),
    }
  }

  function handleSaveError(error: unknown) {
    const rejected =
      error instanceof ApiError &&
      [400, 401, 403, 404, 409, 422].includes(error.status)
    if (policy !== null) {
      if (!rejected || (error instanceof ApiError && error.status === 409))
        setUncertain(true)
      return
    }
    const confirmsNoCommittedCreate =
      error instanceof ApiError && error.code === 'POLICY_ORDER_CONFLICT'
    const identityConflict =
      error instanceof ApiError &&
      error.code === 'PRINTER_EVENT_RULE_IDEMPOTENCY_CONFLICT'
    if (
      rejected &&
      !identityConflict &&
      (!uncertain || confirmsNoCommittedCreate)
    ) {
      setAttemptedCreate(null)
      setUncertain(false)
      return
    }
    setUncertain(true)
  }

  function submit() {
    if (
      save.isPending ||
      reloading ||
      protectedContent ||
      !canManage ||
      !orderReady
    )
      return
    const edit = attemptedCreate ?? buildEdit()
    if (!edit) return
    setLocalError(null)
    if (policy === null) setAttemptedCreate(edit)
    save.mutate(
      { policy, operationId, ruleId, edit },
      { onSuccess: onClose, onError: handleSaveError },
    )
  }

  async function reloadOrder() {
    const refreshed = await order.refetch()
    if (!refreshed.isSuccess) return
    setBeforePolicyId(null)
    save.reset()
  }

  function changeSelector(
    expression: string,
    valid: boolean,
    gui: SelectorGui | null,
  ) {
    setSelectorExpression(expression)
    setSelectorValid(valid)
    setSelectorGui(gui)
  }
  return (
    <form
      className="flex flex-col gap-6"
      aria-label={
        policy ? `Edit ${policy.name}` : 'Create printer event rule policy'
      }
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      <section className="flex flex-col gap-3">
        {policy ? (
          <DialogTitle>{title}</DialogTitle>
        ) : (
          <h2 className="text-sm font-medium">{title}</h2>
        )}
        <TextField
          label="Policy name"
          name="name"
          kind="text"
          onBlur={() => {}}
          value={name}
          maxLength={200}
          required
          disabled={locked}
          onChange={setName}
        />
        {protectedContent && (
          <p role="alert" className="text-xs">
            {restriction}
          </p>
        )}
        <Frame title="Condition and actions">
          <FrameCard className="flex flex-col gap-4 p-4">
            {condition ? (
              <EventConditionFields
                catalog={catalog}
                value={condition}
                onChange={setCondition}
                disabled={locked}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                This rule uses an advanced condition. The saved condition will
                be preserved when you edit its name, targets, or notifications.
              </p>
            )}
            {showActions && (
              <EventNotificationActions
                entityId={entityId}
                value={actions}
                onChange={setActions}
                disabled={locked}
                canAddEmail={canAddEmail}
              />
            )}
          </FrameCard>
        </Frame>
      </section>
      <PolicySelector
        entityId={entityId}
        value={selectorExpression}
        gui={selectorGui}
        targetFields={PRINTER_SELECTOR_FIELDS}
        targetLabel="printers"
        disabled={locked}
        onChange={changeSelector}
      />
      {policy === null && (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-medium">Policy order</h2>
          <p className="text-xs text-muted-foreground">
            Every matching event rule applies. Ordering does not remove other
            matching rules.
          </p>
          {order.isError ? (
            <RetryNotice
              message="Policy order could not be loaded."
              onRetry={() => void order.refetch()}
            />
          ) : !order.data ? (
            <LoadingNotice message="Loading policy order…" />
          ) : (
            <PolicyPositionPicker
              composition="additive"
              policies={order.data}
              name={name}
              beforePolicyId={beforePolicyId}
              disabled={locked}
              onChange={setBeforePolicyId}
            />
          )}
        </section>
      )}
      {(localError || save.error || reloadError) && (
        <p role="alert" className="text-xs text-destructive">
          {localError ?? reloadError ?? saveErrorMessage}
        </p>
      )}
      {uncertain && (
        <p role="alert" className="text-xs">
          The save could not be confirmed.{' '}
          {policy
            ? 'Reload the policy to check its saved state.'
            : 'Retry the same policy before making further changes.'}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          disabled={save.isPending || reloading}
          onClick={onClose}
        >
          Cancel
        </Button>
        {orderConflict && (
          <Button
            type="button"
            variant="outline"
            disabled={order.isFetching}
            onClick={() => void reloadOrder()}
          >
            Reload policy order
          </Button>
        )}
        {policy && onReload && (
          <Button
            type="button"
            variant="outline"
            disabled={save.isPending || reloading}
            onClick={onReload}
          >
            Reload policy
          </Button>
        )}
        <Button
          type="submit"
          disabled={
            save.isPending ||
            reloading ||
            !orderReady ||
            protectedContent ||
            !canManage ||
            (uncertain && policy !== null)
          }
        >
          {saveLabel}
        </Button>
      </div>
    </form>
  )
}
